import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Calendar, momentLocalizer } from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import moment from "moment";
import "moment/locale/it";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { 
  ArrowLeft, Plus, Syringe, Stethoscope, Scissors, Heart, 
  Eye, Bone, CheckCircle, X, Clock, User, PawPrint
} from "lucide-react";

const API = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL || '';

// Setup moment locale
moment.locale('it');
const localizer = momentLocalizer(moment);

// Enable drag and drop
const DnDCalendar = withDragAndDrop(Calendar);

// Service types with colors
const SERVICE_TYPES = {
  visita: { label: "Visita generale", color: "#2E7D32", icon: Stethoscope },
  vaccinazione: { label: "Vaccinazione", color: "#1976D2", icon: Syringe },
  controllo: { label: "Controllo", color: "#7B1FA2", icon: Eye },
  chirurgia: { label: "Chirurgia", color: "#C62828", icon: Scissors },
  cardiologia: { label: "Cardiologia", color: "#D32F2F", icon: Heart },
  ortopedia: { label: "Ortopedia", color: "#F57C00", icon: Bone },
  altro: { label: "Altro", color: "#616161", icon: PawPrint }
};

const DURATION_OPTIONS = [15, 30, 45, 60, 90, 120];

export default function VetCalendar() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [vet, setVet] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  
  // New appointment dialog
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [newAppointment, setNewAppointment] = useState({
    pet_id: "",
    date: "",
    time: "",
    duration_minutes: 30,
    service_type: "visita",
    notes: ""
  });
  const [saving, setSaving] = useState(false);

  // Edit appointment dialog
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('vet_token');
    if (!token) {
      navigate('/vet/login');
      return;
    }

    try {
      const res = await fetch(`${API}/api/vet/me`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      if (res.ok) {
        const data = await res.json();
        setVet(data);
        fetchAppointments(token);
        fetchPatients(token);
      } else {
        navigate('/vet/login');
      }
    } catch (error) {
      navigate('/vet/login');
    }
  };

  const fetchAppointments = async (token, dateFrom = null, dateTo = null) => {
    try {
      let url = `${API}/api/vet/appointments`;
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (params.toString()) url += `?${params.toString()}`;

      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      if (res.ok) {
        const data = await res.json();
        setAppointments(data);
      }
    } catch (error) {
      console.error('Error fetching appointments:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPatients = async (token) => {
    try {
      const res = await fetch(`${API}/api/vet/patients`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setPatients(data);
      }
    } catch (error) {
      console.error('Error fetching patients:', error);
    }
  };

  // Convert appointments to calendar events
  const events = useMemo(() => {
    return appointments.map(apt => {
      const [year, month, day] = apt.date.split('-').map(Number);
      const [hour, minute] = apt.time.split(':').map(Number);
      const start = new Date(year, month - 1, day, hour, minute);
      const end = new Date(start.getTime() + (apt.duration_minutes || 30) * 60000);
      
      return {
        id: apt.appointment_id,
        title: `${apt.pet_name} - ${SERVICE_TYPES[apt.service_type]?.label || apt.service_type}`,
        start,
        end,
        resource: apt
      };
    });
  }, [appointments]);

  // Handle slot selection (create new appointment)
  const handleSelectSlot = useCallback(({ start }) => {
    const dateStr = moment(start).format('YYYY-MM-DD');
    const timeStr = moment(start).format('HH:mm');
    
    setSelectedSlot({ date: dateStr, time: timeStr });
    setNewAppointment({
      pet_id: "",
      date: dateStr,
      time: timeStr,
      duration_minutes: 30,
      service_type: "visita",
      notes: ""
    });
    setShowNewDialog(true);
  }, []);

  // Handle event selection (view/edit)
  const handleSelectEvent = useCallback((event) => {
    setSelectedAppointment(event.resource);
    setShowEditDialog(true);
  }, []);

  // Handle drag and drop
  const handleEventDrop = useCallback(async ({ event, start }) => {
    const token = localStorage.getItem('vet_token');
    const newDate = moment(start).format('YYYY-MM-DD');
    const newTime = moment(start).format('HH:mm');

    try {
      const res = await fetch(`${API}/api/vet/appointments/${event.id}?date=${newDate}&time=${newTime}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      if (res.ok) {
        toast.success("Appuntamento spostato");
        fetchAppointments(token);
      } else {
        toast.error("Errore nello spostamento");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    }
  }, []);

  // Handle event resize
  const handleEventResize = useCallback(async ({ event, start, end }) => {
    // Calculate new duration
    const durationMs = end.getTime() - start.getTime();
    const durationMinutes = Math.round(durationMs / 60000);
    
    // For now, just update date/time. Duration update would need backend change.
    handleEventDrop({ event, start });
  }, [handleEventDrop]);

  // Create new appointment
  const handleCreateAppointment = async () => {
    if (!newAppointment.pet_id) {
      toast.error("Seleziona un paziente");
      return;
    }

    setSaving(true);
    const token = localStorage.getItem('vet_token');

    try {
      const res = await fetch(`${API}/api/vet/appointments`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(newAppointment)
      });

      if (res.ok) {
        toast.success("Appuntamento creato");
        setShowNewDialog(false);
        fetchAppointments(token);
      } else {
        const data = await res.json();
        toast.error(data.detail || "Errore nella creazione");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      setSaving(false);
    }
  };

  // Update appointment status
  const handleUpdateStatus = async (status) => {
    if (!selectedAppointment) return;

    const token = localStorage.getItem('vet_token');
    try {
      const res = await fetch(`${API}/api/vet/appointments/${selectedAppointment.appointment_id}?status=${status}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      if (res.ok) {
        toast.success("Stato aggiornato");
        setShowEditDialog(false);
        fetchAppointments(token);
      } else {
        toast.error("Errore nell'aggiornamento");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    }
  };

  // Delete appointment
  const handleDeleteAppointment = async () => {
    if (!selectedAppointment) return;

    const token = localStorage.getItem('vet_token');
    try {
      const res = await fetch(`${API}/api/vet/appointments/${selectedAppointment.appointment_id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      if (res.ok) {
        toast.success("Appuntamento eliminato");
        setShowEditDialog(false);
        fetchAppointments(token);
      } else {
        toast.error("Errore nell'eliminazione");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    }
  };

  // Custom event styling
  const eventStyleGetter = (event) => {
    const serviceType = event.resource?.service_type || 'altro';
    const color = SERVICE_TYPES[serviceType]?.color || '#616161';
    const isCompleted = event.resource?.status === 'completed';
    const isCancelled = event.resource?.status === 'cancelled';
    
    return {
      style: {
        backgroundColor: isCancelled ? '#9E9E9E' : color,
        borderRadius: '8px',
        opacity: isCompleted ? 0.7 : 1,
        color: 'white',
        border: 'none',
        padding: '2px 8px',
        textDecoration: isCancelled ? 'line-through' : 'none'
      }
    };
  };

  // Custom toolbar messages in Italian
  const messages = {
    today: 'Oggi',
    previous: 'Indietro',
    next: 'Avanti',
    month: 'Mese',
    week: 'Settimana',
    day: 'Giorno',
    agenda: 'Agenda',
    date: 'Data',
    time: 'Ora',
    event: 'Evento',
    noEventsInRange: 'Nessun appuntamento in questo periodo'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-3 border-[#2E7D32] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#86868B]">Caricamento calendario...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      {/* Header */}
      <header className="bg-white border-b border-black/5 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link 
                to="/vet/dashboard" 
                className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
                data-testid="back-to-dashboard"
              >
                <ArrowLeft size={20} className="text-[#86868B]" />
              </Link>
              <div>
                <h1 className="font-bold text-[#1D1D1F]">Calendario Appuntamenti</h1>
                <p className="text-xs text-[#86868B]">{vet?.name}</p>
              </div>
            </div>

            <Button 
              onClick={() => {
                const now = new Date();
                setSelectedSlot({ 
                  date: moment(now).format('YYYY-MM-DD'), 
                  time: moment(now).add(1, 'hour').startOf('hour').format('HH:mm')
                });
                setNewAppointment({
                  pet_id: "",
                  date: moment(now).format('YYYY-MM-DD'),
                  time: moment(now).add(1, 'hour').startOf('hour').format('HH:mm'),
                  duration_minutes: 30,
                  service_type: "visita",
                  notes: ""
                });
                setShowNewDialog(true);
              }}
              className="bg-[#2E7D32] hover:bg-[#1B5E20] rounded-xl"
              data-testid="new-appointment-btn"
            >
              <Plus size={18} className="mr-2" /> Nuovo appuntamento
            </Button>
          </div>
        </div>
      </header>

      {/* Legend */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="bg-white rounded-2xl p-4 shadow-sm mb-4">
          <p className="text-xs text-[#86868B] mb-2">Legenda tipologie:</p>
          <div className="flex flex-wrap gap-3">
            {Object.entries(SERVICE_TYPES).map(([key, { label, color, icon: Icon }]) => (
              <div key={key} className="flex items-center gap-2">
                <div 
                  className="w-4 h-4 rounded"
                  style={{ backgroundColor: color }}
                />
                <Icon size={14} className="text-[#86868B]" />
                <span className="text-xs text-[#1D1D1F]">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Calendar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <div className="bg-white rounded-3xl p-4 sm:p-6 shadow-sm">
          <style>{`
            .rbc-calendar {
              font-family: inherit;
            }
            .rbc-toolbar {
              margin-bottom: 20px;
              flex-wrap: wrap;
              gap: 8px;
            }
            .rbc-toolbar button {
              border-radius: 8px;
              padding: 8px 16px;
              border: 1px solid #E5E5EA;
              background: white;
              color: #1D1D1F;
              font-weight: 500;
            }
            .rbc-toolbar button:hover {
              background: #F5F5F7;
            }
            .rbc-toolbar button.rbc-active {
              background: #2E7D32;
              color: white;
              border-color: #2E7D32;
            }
            .rbc-header {
              padding: 12px 8px;
              font-weight: 600;
              color: #1D1D1F;
              border-bottom: 2px solid #E5E5EA;
            }
            .rbc-time-view {
              border: 1px solid #E5E5EA;
              border-radius: 16px;
              overflow: hidden;
            }
            .rbc-time-header {
              border-bottom: 1px solid #E5E5EA;
            }
            .rbc-time-content {
              border-top: none;
            }
            .rbc-time-slot {
              border-top: 1px solid #F5F5F7;
            }
            .rbc-day-slot .rbc-time-slot {
              border-top: 1px solid #F5F5F7;
            }
            .rbc-timeslot-group {
              min-height: 60px;
            }
            .rbc-current-time-indicator {
              background-color: #C62828;
            }
            .rbc-today {
              background-color: #E8F5E9;
            }
            .rbc-event {
              border: none !important;
            }
            .rbc-event-label {
              display: none;
            }
            .rbc-event-content {
              font-size: 12px;
              font-weight: 500;
            }
            .rbc-addons-dnd-resize-ns-icon {
              display: none;
            }
            .rbc-addons-dnd-resizable {
              cursor: move;
            }
          `}</style>
          
          <DnDCalendar
            localizer={localizer}
            events={events}
            defaultView="week"
            views={['week', 'day', 'agenda']}
            step={15}
            timeslots={4}
            min={new Date(2024, 0, 1, 7, 0)}
            max={new Date(2024, 0, 1, 21, 0)}
            date={currentDate}
            onNavigate={setCurrentDate}
            selectable
            resizable
            onSelectSlot={handleSelectSlot}
            onSelectEvent={handleSelectEvent}
            onEventDrop={handleEventDrop}
            onEventResize={handleEventResize}
            eventPropGetter={eventStyleGetter}
            messages={messages}
            style={{ height: 'calc(100vh - 300px)', minHeight: '500px' }}
            draggableAccessor={() => true}
            data-testid="vet-calendar"
          />
        </div>
      </div>

      {/* New Appointment Dialog */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent className="max-w-md rounded-3xl border-0">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#1D1D1F]">
              Nuovo Appuntamento
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Patient Select */}
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Paziente *</Label>
              <Select 
                value={newAppointment.pet_id} 
                onValueChange={(v) => setNewAppointment({...newAppointment, pet_id: v})}
              >
                <SelectTrigger className="h-12 rounded-xl" data-testid="select-patient">
                  <SelectValue placeholder="Seleziona paziente..." />
                </SelectTrigger>
                <SelectContent>
                  {patients.map(p => (
                    <SelectItem key={p.pet_id} value={p.pet_id}>
                      <div className="flex items-center gap-2">
                        <PawPrint size={16} className="text-[#86868B]" />
                        <span>{p.pet_name}</span>
                        <span className="text-xs text-[#86868B]">({p.owner_name})</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date & Time */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Data *</Label>
                <Input
                  type="date"
                  value={newAppointment.date}
                  onChange={(e) => setNewAppointment({...newAppointment, date: e.target.value})}
                  className="h-12 rounded-xl"
                  data-testid="appointment-date"
                />
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Ora *</Label>
                <Input
                  type="time"
                  value={newAppointment.time}
                  onChange={(e) => setNewAppointment({...newAppointment, time: e.target.value})}
                  className="h-12 rounded-xl"
                  data-testid="appointment-time"
                />
              </div>
            </div>

            {/* Service Type */}
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Tipo prestazione *</Label>
              <Select 
                value={newAppointment.service_type} 
                onValueChange={(v) => setNewAppointment({...newAppointment, service_type: v})}
              >
                <SelectTrigger className="h-12 rounded-xl" data-testid="select-service-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(SERVICE_TYPES).map(([key, { label, color, icon: Icon }]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded" style={{ backgroundColor: color }} />
                        <Icon size={16} className="text-[#86868B]" />
                        <span>{label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Duration */}
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Durata (minuti)</Label>
              <Select 
                value={newAppointment.duration_minutes.toString()} 
                onValueChange={(v) => setNewAppointment({...newAppointment, duration_minutes: parseInt(v)})}
              >
                <SelectTrigger className="h-12 rounded-xl" data-testid="select-duration">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DURATION_OPTIONS.map(d => (
                    <SelectItem key={d} value={d.toString()}>
                      {d} minuti
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Notes */}
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Note</Label>
              <Textarea
                value={newAppointment.notes}
                onChange={(e) => setNewAppointment({...newAppointment, notes: e.target.value})}
                placeholder="Note aggiuntive..."
                className="rounded-xl resize-none"
                rows={3}
                data-testid="appointment-notes"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowNewDialog(false)}
              className="rounded-xl"
            >
              Annulla
            </Button>
            <Button 
              onClick={handleCreateAppointment}
              disabled={saving || !newAppointment.pet_id}
              className="bg-[#2E7D32] hover:bg-[#1B5E20] rounded-xl"
              data-testid="save-appointment-btn"
            >
              {saving ? "Salvataggio..." : "Crea appuntamento"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Appointment Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md rounded-3xl border-0">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#1D1D1F]">
              Dettagli Appuntamento
            </DialogTitle>
          </DialogHeader>

          {selectedAppointment && (
            <div className="py-4">
              {/* Status Badge */}
              <div className="flex items-center gap-2 mb-4">
                {selectedAppointment.status === 'scheduled' && (
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                    Programmato
                  </span>
                )}
                {selectedAppointment.status === 'confirmed' && (
                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                    Confermato
                  </span>
                )}
                {selectedAppointment.status === 'completed' && (
                  <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
                    Completato
                  </span>
                )}
                {selectedAppointment.status === 'cancelled' && (
                  <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
                    Annullato
                  </span>
                )}
              </div>

              {/* Patient Info */}
              <div className="bg-[#F5F5F7] rounded-2xl p-4 mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center">
                    <PawPrint className="text-[#2E7D32]" size={24} />
                  </div>
                  <div>
                    <p className="font-semibold text-[#1D1D1F]">{selectedAppointment.pet_name}</p>
                    <p className="text-sm text-[#86868B]">
                      <User size={12} className="inline mr-1" />
                      {selectedAppointment.owner_name}
                    </p>
                  </div>
                </div>
              </div>

              {/* Appointment Details */}
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Clock className="text-[#86868B]" size={18} />
                  <span className="text-[#1D1D1F]">
                    {selectedAppointment.date} alle {selectedAppointment.time}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  {SERVICE_TYPES[selectedAppointment.service_type]?.icon && (
                    <div 
                      className="w-5 h-5 rounded flex items-center justify-center"
                      style={{ backgroundColor: SERVICE_TYPES[selectedAppointment.service_type]?.color }}
                    >
                      {(() => {
                        const Icon = SERVICE_TYPES[selectedAppointment.service_type]?.icon;
                        return <Icon size={12} className="text-white" />;
                      })()}
                    </div>
                  )}
                  <span className="text-[#1D1D1F]">
                    {SERVICE_TYPES[selectedAppointment.service_type]?.label || selectedAppointment.service_type}
                  </span>
                </div>
                {selectedAppointment.notes && (
                  <div className="p-3 bg-gray-50 rounded-xl text-sm text-[#86868B]">
                    {selectedAppointment.notes}
                  </div>
                )}
              </div>

              {/* Contact Info */}
              {(selectedAppointment.owner_phone || selectedAppointment.owner_email) && (
                <div className="mt-4 p-3 bg-blue-50 rounded-xl">
                  <p className="text-xs text-blue-600 font-medium mb-1">Contatti proprietario</p>
                  {selectedAppointment.owner_phone && (
                    <p className="text-sm text-blue-800">{selectedAppointment.owner_phone}</p>
                  )}
                  {selectedAppointment.owner_email && (
                    <p className="text-sm text-blue-800">{selectedAppointment.owner_email}</p>
                  )}
                </div>
              )}

              {/* Actions */}
              {selectedAppointment.status !== 'completed' && selectedAppointment.status !== 'cancelled' && (
                <div className="mt-6 space-y-2">
                  <div className="flex gap-2">
                    <Button 
                      onClick={() => handleUpdateStatus('completed')}
                      className="flex-1 bg-[#2E7D32] hover:bg-[#1B5E20] rounded-xl"
                      data-testid="complete-appointment-btn"
                    >
                      <CheckCircle size={18} className="mr-2" /> Completa
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => handleUpdateStatus('cancelled')}
                      className="flex-1 rounded-xl text-red-600 border-red-200 hover:bg-red-50"
                      data-testid="cancel-appointment-btn"
                    >
                      <X size={18} className="mr-2" /> Annulla
                    </Button>
                  </div>
                  <Button 
                    variant="ghost"
                    onClick={handleDeleteAppointment}
                    className="w-full rounded-xl text-red-600 hover:bg-red-50"
                    data-testid="delete-appointment-btn"
                  >
                    Elimina appuntamento
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
